import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Search, BookOpen, Eye } from "lucide-react";
import { Button } from "@/components/ui/button";

export const KnowledgeBase = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedArticle, setSelectedArticle] = useState<any>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const { data: articles, isLoading } = useQuery({
    queryKey: ["knowledge-base", searchTerm],
    queryFn: async () => {
      let query = supabase
        .from("knowledge_base")
        .select("*")
        .eq("is_published", true)
        .order("created_at", { ascending: false });

      if (searchTerm) {
        query = query.or(`title.ilike.%${searchTerm}%,content.ilike.%${searchTerm}%`);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data;
    },
  });

  const categories = [...new Set(articles?.map(a => a.category) || [])];

  const openArticle = (article: any) => {
    setSelectedArticle(article);
    setDialogOpen(true);
    
    // Increment view count
    supabase
      .from('knowledge_base')
      .update({ views: article.views + 1 })
      .eq('id', article.id)
      .then();
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      <div>
        <h2 className="text-xl sm:text-2xl font-bold">Knowledge Base</h2>
        <p className="text-sm sm:text-base text-muted-foreground mt-1">Find helpful guides and resources</p>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search articles..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10 h-11 sm:h-12"
        />
      </div>

      {/* Categories */}
      {categories.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {categories.map(category => (
            <Badge key={category} variant="secondary" className="capitalize text-xs sm:text-sm">
              {category.replace(/-/g, ' ')}
            </Badge>
          ))}
        </div>
      )}

      {/* Articles */}
      {isLoading ? (
        <div className="text-center py-12">
          <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full mx-auto"></div>
          <p className="text-muted-foreground mt-4">Loading articles...</p>
        </div>
      ) : !articles || articles.length === 0 ? (
        <Card className="glass-card p-8 sm:p-12">
          <div className="text-center">
            <BookOpen className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-base sm:text-lg text-muted-foreground">No articles found</p>
            <p className="text-xs sm:text-sm text-muted-foreground mt-2">Try adjusting your search terms</p>
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
          {articles.map((article: any) => (
            <Card
              key={article.id}
              className="glass-card-hover p-4 sm:p-6 cursor-pointer transition-all hover-scale"
              onClick={() => openArticle(article)}
            >
              <div className="flex items-start justify-between mb-3">
                <BookOpen className="h-5 w-5 text-primary shrink-0" />
                <Badge variant="outline" className="capitalize text-xs">
                  {article.category.replace(/-/g, ' ')}
                </Badge>
              </div>
              <h3 className="font-semibold text-base sm:text-lg mb-2 line-clamp-2">{article.title}</h3>
              <p className="text-xs sm:text-sm text-muted-foreground line-clamp-3 mb-4">
                {article.content.substring(0, 150)}...
              </p>
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <div className="flex items-center gap-1">
                  <Eye className="h-3 w-3" />
                  {article.views} views
                </div>
                {article.tags && article.tags.length > 0 && (
                  <div className="flex gap-1 flex-wrap">
                    {article.tags.slice(0, 2).map((tag: string) => (
                      <Badge key={tag} variant="secondary" className="text-xs">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Article Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl sm:text-2xl pr-8">{selectedArticle?.title}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center gap-2 flex-wrap">
              <Badge variant="outline" className="capitalize text-xs sm:text-sm">
                {selectedArticle?.category.replace(/-/g, ' ')}
              </Badge>
              <span className="text-xs sm:text-sm text-muted-foreground flex items-center gap-1">
                <Eye className="h-3 w-3" />
                {selectedArticle?.views} views
              </span>
            </div>
            <div className="prose prose-sm sm:prose-base dark:prose-invert max-w-none">
              <div className="whitespace-pre-wrap text-sm sm:text-base">{selectedArticle?.content}</div>
            </div>
            {selectedArticle?.tags && selectedArticle.tags.length > 0 && (
              <div className="flex flex-wrap gap-2 pt-4 border-t">
                {selectedArticle.tags.map((tag: string) => (
                  <Badge key={tag} variant="secondary" className="text-xs sm:text-sm">
                    {tag}
                  </Badge>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
